import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { getAccessToken, RETAIL_BASE_URL } from "../components/Retails/api/retailApi";


/** One message from the retail hub. `event` is e.g. "entity.updated", "sale.completed", "analytics.live_kpis". */
export interface RetailEvent {
  event: string;
  module: string;
  entityType: string;
  entityId?: string | null;
  action: string;
  storeId?: string | null;
  data?: unknown;
  occurredAtUtc: string;
  correlationId?: string | null;
}

type Listener = (e: RetailEvent) => void;

const listeners = new Set<Listener>();
let connection: signalR.HubConnection | null = null;
let starting: Promise<void> | null = null;
let users = 0;

function connect(): Promise<void> {
  if (!connection) {
    connection = new signalR.HubConnectionBuilder()
      .withUrl(`${RETAIL_BASE_URL}/hubs/retail`, { accessTokenFactory: () => getAccessToken() ?? "" })
      .withAutomaticReconnect()
      .build();
    connection.on("event", (e: RetailEvent) => listeners.forEach((l) => l(e)));
  }
  if (connection.state === signalR.HubConnectionState.Disconnected && !starting) {
    starting = connection
      .start()
      .catch((err) => console.warn("Retail realtime could not connect", err))
      .finally(() => {
        starting = null;
      });
  }
  return starting ?? Promise.resolve();
}

async function disconnect() {
  const c = connection;
  if (!c) return;
  connection = null;
  try {
    await starting;
    await c.stop();
  } catch {
    /* nothing to clean up */
  }
}

/**
 * Subscribes the component to the retail hub. One shared connection serves every component that uses this hook,
 * and it closes when the last one unmounts.
 */
export function useRetailEvents(onEvent: Listener) {
  const latest = useRef(onEvent);
  latest.current = onEvent;

  useEffect(() => {
    const listener: Listener = (e) => latest.current(e);
    listeners.add(listener);
    users += 1;
    void connect();
    return () => {
      listeners.delete(listener);
      users -= 1;
      if (users === 0) void disconnect();
    };
  }, []);
}